import { CallDetailOverviewRow, CallOverviewColumnFilterItem } from '@/types/call-detail-overview';
import axiosInstance from '../config/axiosConfig';
import { PaginatedResponse } from '../types';
import { CallDetailOverviewPagedRequest } from "../types/request";
import { CallDirectionTypes } from '@/enums/call-direction';
import { toUtcISOString } from '@/utils/callJourneyTimeFilter';

function normalizeUtcTimestampToken(value: string): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
        return raw;
    }

    const dotNetEpochRegex = /^\/Date\((\d+)\)\/$/;
    const dotNetEpochMatch = dotNetEpochRegex.exec(raw);
    if (dotNetEpochMatch) {
        const epochDate = new Date(Number(dotNetEpochMatch[1]));
        return Number.isNaN(epochDate.getTime()) ? raw : epochDate.toISOString();
    }

    if (/^\d{10,13}$/.test(raw)) {
        const asNumber = Number(raw.length === 10 ? `${raw}000` : raw);
        const epochDate = new Date(asNumber);
        return Number.isNaN(epochDate.getTime()) ? raw : epochDate.toISOString();
    }

    let normalized = raw.replace(' ', 'T');
    normalized = normalized.replace(/\.(\d{3})\d+/, '.$1');

    // Backend startTime filter values are UTC; ensure timezone suffix is explicit.
    if (!/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(normalized)) {
        normalized = `${normalized}Z`;
    }

    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toISOString();
}

function mapSubFilterValues(columnName: string, values: string[]): string[] {
    if (columnName === 'voicemail') {
        return values.map((v) => {
            if (v === 'Yes') return 'true';
            if (v === 'No') return 'false';
            return v;
        });
    }

    if (columnName === 'startTime') {
        return values.map((v) => normalizeUtcTimestampToken(String(v)));
    }

    return values;
}

function buildOverviewPayload(params: CallDetailOverviewPagedRequest) {
    return {
        startTime: toUtcISOString(params.startDate),
        endTime: toUtcISOString(params.endDate),
        queueMicrosoftIds: params.queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: params.agentMicrosoftIds ?? undefined,
        groupIds: params.groupIds ?? null,
        page: params.page ?? null,
        limit: params.limit ?? null,
        direction: params.callDirectionTypes ?? undefined,
        callMicrosoftIds: params.callMicrosoftIds ?? undefined,
        agentDisplayNameTransfer: params.agentDisplayNameTransfer ?? undefined,
        sortTable: params.sort
            ? {
                columnName: params.sort.column,
                ColumnValue: params.sort.direction,
            }
            : undefined,
        subFilter: params.filters?.map((f) => ({
            columnName: f.column,
            columnValue: f.value,
        })) ?? undefined,
    };
}

async function fetchCallDetailOverviewPaged({
    startDate,
    endDate,
    queueMicrosoftIds,
    agentMicrosoftIds,
    groupIds,
    page = null,
    limit = null,
    callDirectionTypes,
    sort,
    filters,
    callMicrosoftIds,
    agentDisplayNameTransfer
}: CallDetailOverviewPagedRequest, options?: { signal?: AbortSignal }): Promise<PaginatedResponse<CallDetailOverviewRow>> {
    const payload = buildOverviewPayload({
        startDate,
        endDate,
        queueMicrosoftIds,
        agentMicrosoftIds,
        groupIds,
        page,
        limit,
        callDirectionTypes,
        callMicrosoftIds,
        agentDisplayNameTransfer,
        sort,
        filters,
    });
    const res = await axiosInstance.post<PaginatedResponse<CallDetailOverviewRow>>(`/call-widget/overview`, payload, { signal: options?.signal });
    const { data: rawData, total } = res.data;

    // Map voicemail from API (boolean); support voicemail | voiceMail | voice_mail
    const data: CallDetailOverviewRow[] = (rawData || []).map((row: CallDetailOverviewRow & { voiceMail?: boolean; voice_mail?: boolean }) => ({
        ...row,
        voicemail: row.voicemail ?? row.voiceMail ?? row.voice_mail ?? false,
    }));

    return {
        data,
        total: total ?? 0,
        page: page ?? 1,
        limit: limit ?? 0,
        totalPages: limit && total ? Math.ceil(total / limit) : 1,
    };
}

async function fetchCallDetailOverviewColumnFilter({
    startDate,
    endDate,
    queueMicrosoftIds,
    agentMicrosoftIds,
    groupIds,
    callDirectionTypes,
    page,
    limit,
    filterColumns = [],
    callMicrosoftIds,
    appliedFilters = {}
}: {
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
    queueMicrosoftIds: string[];
    agentMicrosoftIds: string[];
    groupIds: number[];
    page: number;
    limit: number;
    callDirectionTypes: CallDirectionTypes[];
    filterColumns: {
        columnName: string;
        searchValue?: string | null;
    }[];
    callMicrosoftIds?: string[];
    appliedFilters?: Record<string, string[]>;
}, options?: { signal?: AbortSignal }): Promise<CallOverviewColumnFilterItem[]> {

    const payload = {
        startTime: toUtcISOString(startDate),
        endTime: toUtcISOString(endDate),
        queueMicrosoftIds: queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: agentMicrosoftIds ?? undefined,
        groupIds: groupIds ?? null,
        direction: callDirectionTypes ?? undefined,
        callMicrosoftIds: callMicrosoftIds ?? undefined,
        page: page,
        limit: limit,
        tableFilter: filterColumns.map(f => ({
            columnName: f.columnName,
            columnValue: f.searchValue
        })),
        subFilter: Object.entries(appliedFilters || {})
            .filter(([_, values]) => Array.isArray(values) && values.length > 0)
            .map(([columnName, values]) => ({
                columnName,
                columnValue: mapSubFilterValues(columnName, values)
            })),
    };
    const res = await axiosInstance.post<{ data: CallOverviewColumnFilterItem[] }>(`/call-widget/filters`, payload, { signal: options?.signal });
    const { data } = res.data;
    return (data || []).map((item) =>
        item.columnName === 'startTime'
            ? {
                ...item,
                columnValue: (item.columnValue || []).map(v => normalizeUtcTimestampToken(String(v))),
            }
            : item
    );
}

/* istanbul ignore next: module export */
export { fetchCallDetailOverviewPaged, fetchCallDetailOverviewColumnFilter };
